const EMOJI_COMPONENT_GROUP = 2;

export type EmojiCatalogEntry = {
    readonly hexcode: string;
    readonly label: string;
    readonly order: number;
    readonly shortcodes: ReadonlyArray<string>;
    readonly tags: ReadonlyArray<string>;
    readonly unicode: string;
};

export type EmojiCatalogSourceRow = {
    readonly group?: number;
    readonly hexcode: string;
    readonly label: string;
    readonly order?: number;
    readonly tags?: ReadonlyArray<string>;
    readonly unicode: string;
};

export type EmojiShortcodeDataset = Readonly<
    Record<string, string | ReadonlyArray<string>>
>;

export type EmojiShortcodeToken = {
    readonly complete: boolean;
    readonly end: number;
    readonly query: string;
    readonly start: number;
};

export type TextRangeReplacement = {
    readonly cursor: number;
    readonly value: string;
};

function shortcodeFromLabel(label: string): string {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9+-]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function normalizeSearchQuery(query: string): string {
    return query
        .trim()
        .toLowerCase()
        .replace(/^:+|:+$/g, '')
        .replace(/\s+/g, '_');
}

function shortcodesForHexcode(
    dataset: EmojiShortcodeDataset,
    hexcode: string,
): ReadonlyArray<string> {
    const value = dataset[hexcode];
    if (value === undefined) {
        return [];
    }
    return typeof value === 'string' ? [value] : value;
}

/** Builds the searchable catalog while excluding non-display components. */
export function buildEmojiCatalog(
    rows: ReadonlyArray<EmojiCatalogSourceRow>,
    shortcodeDataset: EmojiShortcodeDataset,
): ReadonlyArray<EmojiCatalogEntry> {
    return rows
        .filter(
            (row): row is EmojiCatalogSourceRow & { readonly group: number } =>
                row.group !== undefined && row.group !== EMOJI_COMPONENT_GROUP,
        )
        .map((row) => {
            const aliases = [
                ...shortcodesForHexcode(shortcodeDataset, row.hexcode),
                shortcodeFromLabel(row.label),
            ].filter(Boolean);
            return {
                hexcode: row.hexcode,
                label: row.label,
                order: row.order ?? Number.MAX_SAFE_INTEGER,
                shortcodes: [...new Set(aliases)],
                tags: row.tags ?? [],
                unicode: row.unicode,
            };
        })
        .sort((left, right) => left.order - right.order);
}

function matchRank(entry: EmojiCatalogEntry, query: string): number | null {
    const aliases = entry.shortcodes.map(normalizeSearchQuery);
    const label = normalizeSearchQuery(entry.label);
    const tags = entry.tags.map(normalizeSearchQuery);

    if (aliases.includes(query)) {
        return 0;
    }
    const closestAliasPrefix = aliases
        .filter((alias) => alias.startsWith(query))
        .reduce<number | null>(
            (closest, alias) =>
                Math.min(closest ?? Number.MAX_SAFE_INTEGER, alias.length),
            null,
        );
    if (closestAliasPrefix !== null) {
        return 100 + Math.min(closestAliasPrefix - query.length, 99);
    }
    if (label.startsWith(query)) {
        return 300 + Math.min(label.length - query.length, 99);
    }
    if (tags.some((tag) => tag.startsWith(query))) {
        return 400;
    }
    if (aliases.some((alias) => alias.includes(query))) {
        return 500;
    }
    if (label.includes(query) || tags.some((tag) => tag.includes(query))) {
        return 600;
    }
    return null;
}

/** Searches shortcode aliases first, then English labels and tags. */
export function searchEmojiCatalog(
    catalog: ReadonlyArray<EmojiCatalogEntry>,
    query: string,
    limit = 200,
): ReadonlyArray<EmojiCatalogEntry> {
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery) {
        return catalog.slice(0, limit);
    }

    return catalog
        .map((entry) => ({ entry, rank: matchRank(entry, normalizedQuery) }))
        .filter(
            (
                match,
            ): match is {
                readonly entry: EmojiCatalogEntry;
                readonly rank: number;
            } => match.rank !== null,
        )
        .sort(
            (left, right) =>
                left.rank - right.rank || left.entry.order - right.entry.order,
        )
        .slice(0, limit)
        .map(({ entry }) => entry);
}

export function findExactEmojiShortcode(
    catalog: ReadonlyArray<EmojiCatalogEntry>,
    query: string,
): EmojiCatalogEntry | null {
    const normalizedQuery = normalizeSearchQuery(query);
    return (
        catalog.find((entry) =>
            entry.shortcodes.some(
                (shortcode) =>
                    normalizeSearchQuery(shortcode) === normalizedQuery,
            ),
        ) ?? null
    );
}

/** Finds a colon token ending at the current textarea caret. */
export function findEmojiShortcodeToken(
    value: string,
    cursor: number,
): EmojiShortcodeToken | null {
    const beforeCursor = value.slice(0, cursor);
    const match = /(^|[\s([{"'])(:([A-Za-z0-9_+-]*)(:?))$/.exec(beforeCursor);
    if (!match) {
        return null;
    }

    const token = match[2];
    const query = match[3];
    const closingColon = match[4];
    if (token === undefined || query === undefined) {
        return null;
    }
    return {
        complete: closingColon === ':',
        end: cursor,
        query,
        start: cursor - token.length,
    };
}

/** Returns null instead of splitting an emoji when the draft is at its limit. */
export function replaceTextRange(
    value: string,
    start: number,
    end: number,
    replacement: string,
    maxLength: number,
): TextRangeReplacement | null {
    if (start < 0 || end < start || end > value.length) {
        throw new RangeError('Replacement range must be inside the draft');
    }
    const nextValue = `${value.slice(0, start)}${replacement}${value.slice(end)}`;
    if (nextValue.length > maxLength) {
        return null;
    }
    return {
        cursor: start + replacement.length,
        value: nextValue,
    };
}

let emojiCatalogPromise: Promise<ReadonlyArray<EmojiCatalogEntry>> | null =
    null;

/** Loads the packaged Unicode data once, on the first emoji interaction. */
export function loadEmojiCatalog(): Promise<ReadonlyArray<EmojiCatalogEntry>> {
    emojiCatalogPromise ??= Promise.all([
        import('emojibase-data/en/compact.json'),
        import('emojibase-data/en/shortcodes/iamcal.json'),
    ])
        .then(([emojiModule, shortcodeModule]) =>
            buildEmojiCatalog(emojiModule.default, shortcodeModule.default),
        )
        .catch((error: unknown) => {
            // A deploy can briefly invalidate a lazy chunk. Do not pin that
            // transient failure for the rest of the browser session.
            emojiCatalogPromise = null;
            throw error;
        });
    return emojiCatalogPromise;
}
